"use client";

import { useMemo, useState } from "react";
import { SearchX } from "lucide-react";

import type { MatchEnvMap } from "@acme/ast-grep-wasm-loader";
import type { AstGrepMatch } from "@acme/validators";
import { cn } from "@acme/ui";
import { Accordion } from "@acme/ui/accordion";
import { ScrollArea } from "@acme/ui/scroll-area";

import { MatchListItem } from "./match-list-item";

interface MatchListProps {
  matchedHighlights: AstGrepMatch<MatchEnvMap>[];
  setHoveredMatch: (match: AstGrepMatch<MatchEnvMap> | null) => void;
  hoveredMatch: AstGrepMatch<MatchEnvMap> | null;
  clearSelectedNode: () => void;
  lang: string;
}

export function MatchList({
  matchedHighlights,
  setHoveredMatch,
  hoveredMatch,
  clearSelectedNode,
  lang,
}: MatchListProps) {
  const defaultValues = useMemo(
    () => matchedHighlights.map((match) => String(match.id)),
    [matchedHighlights],
  );
  const [collapsedMatches, setCollapedMatches] = useState<Set<string>>(
    new Set<string>(),
  );

  const toggleMatch = (id: string) => {
    setCollapedMatches((prev) => {
      if (prev.has(id)) {
        prev.delete(id);
      } else {
        prev.add(id);
      }
      return new Set(prev);
    });
  };

  return (
    <ScrollArea className="h-[calc(100%-2.5rem)]">
      <div
        className={cn(
          "flex min-h-full flex-col justify-start gap-3 p-4",
          matchedHighlights.length === 0 && "justify-center",
        )}
      >
        {matchedHighlights.length === 0 ? (
          <div className="flex size-full flex-col items-center justify-center text-center">
            <SearchX className="text-muted-foreground mb-4 h-12 w-12 opacity-50" />
            <h3 className="mb-2 text-lg font-medium">No matches found</h3>
            <p className="text-muted-foreground text-sm">
              Try adjusting your pattern or rule to find matches in the code.
            </p>
          </div>
        ) : (
          <Accordion type="multiple" defaultValue={defaultValues}>
            <div>
              {matchedHighlights.map((match, index) => {
                const isOpen = !collapsedMatches.has(String(match.id));
                const title = `${index + 1}/${matchedHighlights.length}`;

                return (
                  <MatchListItem
                    key={match.id}
                    match={match}
                    isOpen={isOpen}
                    setHoveredMatch={setHoveredMatch}
                    hoveredMatch={hoveredMatch}
                    clearSelectedNode={clearSelectedNode}
                    onToggle={() => toggleMatch(String(match.id))}
                    language={lang}
                    title={title}
                  />
                );
              })}
            </div>
          </Accordion>
        )}
      </div>
    </ScrollArea>
  );
}
